import React from 'react';
import BlurFade from '../primitives/BlurFade';
import { Section } from '../layout/Section';
import { Container } from '../layout/Container';
import { Eyebrow, Headline, Body } from '../layout/typography';
import { useLang } from '../../LangContext';
import { mediaAlt } from '../../lib/mediaSeo';

/**
 * StepsBlock — numbered process steps with connecting line + small photo per step.
 * items: [{ n, t, d, image? }]
 */
export default function StepsBlock({
  id, testId, tone = 'sand', rhythm = 'relaxed',
  eyebrow, title, items = [], cta,
}) {
  const { lang, overlay } = useLang();
  return (
    <Section id={id} data-testid={testId} tone={tone} rhythm={rhythm}>
      <Container>
        <div className="max-w-3xl mb-16 lg:mb-24">
          {eyebrow && <BlurFade><Eyebrow className="mb-7">{eyebrow}</Eyebrow></BlurFade>}
          {title && <BlurFade delay={0.05}><Headline size="xl">{title}</Headline></BlurFade>}
        </div>

        <div className="relative grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-y-14 md:gap-x-8 lg:gap-x-10">
          {/* connecting dashed line on desktop */}
          <div
            aria-hidden="true"
            className="hidden lg:block absolute top-[120px] left-[12.5%] right-[12.5%] h-px"
            style={{ backgroundImage: 'linear-gradient(90deg, rgba(90,128,86,0.45) 0 6px, transparent 6px 12px)', backgroundSize: '12px 1px' }}
          />

          {items.map((s, i) => (
            <BlurFade key={`step-${s.n}-${s.t}`} delay={i * 0.1} offset={20}>
              <div data-testid={`werkwijze-step-${i + 1}`} className="relative flex flex-col items-start">
                {/* photo */}
                {s.image && (
                  <div className="relative w-full aspect-[5/4] rounded-2xl overflow-hidden bg-white mb-7 shadow-[0_18px_40px_-22px_rgba(31,29,26,0.18)]">
                    <img src={s.image} alt={mediaAlt(overlay, s.image, lang, s.t || '')} className="absolute inset-0 w-full h-full object-cover" />
                  </div>
                )}

                {/* number circle — sits over the photo bottom-left if image present, else at top */}
                <div className={`flex items-center gap-4 ${s.image ? '-mt-12 ml-3 mb-5' : 'mb-5'}`}>
                  <span className="relative z-10 inline-flex items-center justify-center w-14 h-14 rounded-full bg-white border border-[#F26B1A]/30 font-mono text-[15px] text-[#F26B1A] shadow-[0_8px_22px_-10px_rgba(90,128,86,0.45)]">
                    {s.n}
                  </span>
                  <span className="text-[11px] uppercase tracking-[0.18em] text-[#F26B1A]/80 font-medium">
                    Stap {i + 1} / {items.length}
                  </span>
                </div>

                <h3 className="font-display text-[#1F1D1A] text-[24px] lg:text-[30px] leading-tight">{s.t}</h3>
                <Body className="mt-4 max-w-xs">{s.d}</Body>
              </div>
            </BlurFade>
          ))}
        </div>

        {cta && (
          <BlurFade delay={0.4} className="mt-14 lg:mt-20">
            <a href={cta.href} data-testid={cta.testId} data-lead-source={cta.source}
               className="pill pill-leaf inline-flex">
              {cta.label}
            </a>
          </BlurFade>
        )}
      </Container>
    </Section>
  );
}
